import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Logo from "../components/Logo";
import { toast } from "sonner";
import {
  Lock,
  ArrowRight,
  Loader2,
  CheckCircle2,
  TrendingUp,
  Package,
  Users,
  ShieldCheck,
  Zap,
  Gift,
  Bell,
  Clock,
  RefreshCw,
  Store,
  Building2,
  AlertTriangle,
  PlugZap
} from "lucide-react";

// ============ AGENCY MODE COPY ============
// Paste exactly as specified - do not modify

const AGENCY_LOCKED_INSIGHTS = [
  {
    id: "aov",
    title: "AOV opportunity: bundling or thresholds",
    body: "Customers often complete orders with single items when multi-item purchases are common in this category. This typically points to a missing or weak upsell structure.",
    agencyHelper: "Agencies usually address this with bundles, thresholds, or PDP tweaks.",
    lockLabel: "Full recommendation unlocked",
    icon: TrendingUp,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400",
    label: "Revenue Opportunity"
  },
  {
    id: "retention",
    title: "Repeat customer gap",
    body: "Returning customers tend to spend more over time, but this store has room to increase repeat purchase behavior.",
    agencyHelper: "Often driven by post-purchase flows or offer sequencing.",
    lockLabel: "Full recommendation unlocked",
    icon: Users,
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-400",
    label: "Retention Gap"
  },
  {
    id: "inventory",
    title: "Best-seller stock risk",
    body: "One or more top-selling products may be at risk of running low, which can silently cap revenue if not caught early.",
    agencyHelper: "Agencies monitor this to avoid lost sales during active campaigns.",
    lockLabel: "Full recommendation unlocked",
    icon: Package,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400",
    label: "Inventory Alert",
    optional: true // Only show if real signal exists
  }
];

// ============ MERCHANT MODE - Build from real data ============

const buildMerchantInsights = (insightData) => {
  const insights = [];
  
  if (!insightData || insightData.is_processing) {
    return insights;
  }
  
  // Card 1: AOV Insight
  if (insightData.aov_insight) {
    insights.push({
      id: "aov",
      title: insightData.aov_insight.title || "Order Value Optimization",
      whyItMatters: insightData.aov_insight.description || "Opportunities to increase your average order value have been identified.",
      icon: TrendingUp,
      iconBg: "bg-emerald-500/10",
      iconColor: "text-emerald-400",
      label: "Revenue Opportunity",
      hasRealData: !!insightData.aov_insight.current_aov
    });
  }
  
  // Card 2: Product Opportunity
  if (insightData.product_opportunity) {
    insights.push({
      id: "product",
      title: insightData.product_opportunity.title || "Product Mix Analysis",
      whyItMatters: insightData.product_opportunity.description || "Your product performance patterns have been analyzed.",
      icon: Package,
      iconBg: "bg-amber-500/10",
      iconColor: "text-amber-400",
      label: "Product Insight",
      hasRealData: insightData.product_opportunity.products_analyzed > 0
    });
  }
  
  // Card 3: Revenue Leak
  if (insightData.revenue_leak && insightData.revenue_leak.title !== insightData.biggest_insight?.title) {
    insights.push({
      id: "revenue",
      title: insightData.revenue_leak.title || "Revenue Recovery",
      whyItMatters: insightData.revenue_leak.description || "We found areas where revenue may be slipping through.",
      icon: Users,
      iconBg: "bg-blue-500/10",
      iconColor: "text-blue-400",
      label: "Retention Gap",
      hasRealData: !!insightData.revenue_leak.estimated_impact
    });
  }
  
  return insights;
};

const OnboardingPreview = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [insightData, setInsightData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [showBillingPending, setShowBillingPending] = useState(false);
  const [notifyRegistered, setNotifyRegistered] = useState(false);
  const [registeringIntent, setRegisteringIntent] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Rate limiting state - prevent request spam
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [retryAfter, setRetryAfter] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  
  // Account type state
  const [accountType, setAccountType] = useState("merchant");
  const [accountTypeSet, setAccountTypeSet] = useState(false);
  const [showAccountTypePrompt, setShowAccountTypePrompt] = useState(false);
  const [settingAccountType, setSettingAccountType] = useState(false);
  
  // Cooldown timer effect
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setCooldownRemaining(prev => {
        if (prev <= 1) {
          setIsRateLimited(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [cooldownRemaining]);
  
  // Check affiliate status on mount
  useEffect(() => {
    const checkAffiliateStatus = async () => {
      try {
        const res = await api.get("/affiliates/me");
        setIsAffiliate(res.data?.is_affiliate === true);
      } catch (err) {
        setIsAffiliate(false);
      }
    };
    checkAffiliateStatus();
  }, []);

  // Check billing intent status
  useEffect(() => {
    const checkBillingIntent = async () => {
      try {
        const res = await api.get("/billing/intent-status");
        setBillingEnabled(res.data?.billing_enabled !== false);
        setNotifyRegistered(res.data?.notify_on_billing === true);
      } catch (err) {
        setBillingEnabled(true);
      }
    };
    checkBillingIntent();
  }, []);
  
  // Check account type on mount
  useEffect(() => {
    const checkAccountType = async () => {
      // Check localStorage first to avoid flicker
      const cachedType = localStorage.getItem("rd_account_type");
      if (cachedType) {
        setAccountType(cachedType);
        setAccountTypeSet(true);
      }
      
      try {
        const res = await api.get("/users/account-type");
        const serverType = res.data?.account_type || "merchant";
        const serverTypeSet = res.data?.account_type_set || false;
        
        setAccountType(serverType);
        setAccountTypeSet(serverTypeSet);
        
        // Update cache
        if (serverTypeSet) {
          localStorage.setItem("rd_account_type", serverType);
        }
      } catch (err) {
        // Silently fail - don't break the flow
        console.error("Failed to check account type:", err);
      }
    };
    checkAccountType();
  }, []);
  
  // Fetch real insight data from API with rate limit protection
  const fetchInsightData = async (storeId, isRetry = false) => {
    // Guard: prevent multiple simultaneous requests
    if (isAnalyzing) {
      console.log("Request already in flight, skipping");
      return false;
    }
    
    // Guard: enforce minimum cooldown between requests (15 seconds)
    const now = Date.now();
    const minInterval = 15000; // 15 seconds minimum between requests
    const timeSinceLastRequest = now - lastRequestTime;
    
    if (timeSinceLastRequest < minInterval && lastRequestTime > 0) {
      const waitTime = Math.ceil((minInterval - timeSinceLastRequest) / 1000);
      console.log(`Rate limit cooldown: wait ${waitTime}s`);
      setCooldownRemaining(waitTime);
      setIsRateLimited(true);
      return false;
    }
    
    // Guard: respect server-provided retry_after
    if (retryAfter > 0 && isRetry) {
      console.log(`Server retry_after active: ${retryAfter}s remaining`);
      return false;
    }
    
    setIsAnalyzing(true);
    setLastRequestTime(now);
    
    try {
      const res = await api.post("/onboarding/instant-insight", { store_id: storeId });
      
      // Clear rate limit state on success
      setRetryAfter(0);
      setIsRateLimited(false);
      
      // P0: Handle needs_reauth - show reconnect prompt
      if (res.data?.needs_reauth || res.data?.status === "needs_reauth") {
        setInsightData({ 
          needs_reauth: true, 
          message: res.data.message || "Shopify connection expired. Please reconnect your store.",
          store_id: storeId
        });
        return false;
      }
      
      // Handle insufficient data - allow subscription but show clear message
      if (res.data?.is_insufficient_data || res.data?.status === "insufficient_data") {
        setInsightData({ 
          is_insufficient_data: true, 
          message: res.data.message,
          can_subscribe: true,
          store_id: storeId
        });
        return false;
      }
      
      if (res.data?.is_processing || res.data?.status === "processing") {
        setInsightData({ is_processing: true, message: res.data.message });
        return false;
      }
      
      setInsightData(res.data);
      
      // Show account type prompt after first insight (only if not already set)
      if (!accountTypeSet && res.data?.biggest_insight) {
        setShowAccountTypePrompt(true);
      }
      
      return true;
    } catch (err) {
      console.error("Failed to fetch insight data:", err);
      
      // Handle 429 rate limit response
      if (err.response?.status === 429) {
        const serverRetryAfter = err.response?.data?.retry_after || 
                                 parseInt(err.response?.headers?.['retry-after']) || 
                                 60;
        
        console.log(`Rate limited (429). Retry after ${serverRetryAfter}s`);
        setRetryAfter(serverRetryAfter);
        setCooldownRemaining(serverRetryAfter);
        setIsRateLimited(true);
        
        setInsightData({ 
          is_processing: true, 
          is_rate_limited: true,
          message: err.response?.data?.message || `Please wait ${serverRetryAfter} seconds before trying again.`,
          retry_after: serverRetryAfter
        });
        return false;
      }
      
      // Other errors - show processing state
      setInsightData({ 
        is_processing: true, 
        message: "We're analyzing your store data. This may take a moment." 
      });
      return false;
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Get instant insight data - runs once on mount
  useEffect(() => {
    let mounted = true;
    
    const checkAccess = async () => {
      if (!mounted) return;
      
      try {
        const storesRes = await api.get("/shopify/stores");
        const stores = storesRes.data?.stores || [];
        
        if (stores.length === 0) {
          navigate("/onboarding", { replace: true });
          return;
        }
        
        const billingRes = await api.get("/billing/status");
        const status = billingRes.data?.status;
        
        if (status === "active" || status === "trialing" || status === "lifetime") {
          navigate("/dashboard", { replace: true });
          return;
        }
        
        if (location.state?.insightData && !location.state.insightData.is_processing) {
          setInsightData(location.state.insightData);
          // Show account type prompt if not already set
          if (!accountTypeSet && location.state.insightData?.biggest_insight) {
            setShowAccountTypePrompt(true);
          }
          setLoading(false);
          return;
        }
        
        const storeId = stores[0]?.store_id;
        if (storeId && mounted) {
          await fetchInsightData(storeId, false);
        }
        
        if (mounted) {
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to check access:", error);
        if (mounted) {
          navigate("/onboarding", { replace: true });
        }
      }
    };
    
    if (user) {
      checkAccess();
    }
    
    // Cleanup to prevent state updates on unmounted component
    return () => {
      mounted = false;
    };
  }, [user, navigate, location]);

  // Handle account type selection
  const handleSetAccountType = async (type) => {
    setSettingAccountType(true);
    
    // Optimistic update
    setAccountType(type);
    setAccountTypeSet(true);
    setShowAccountTypePrompt(false);
    localStorage.setItem("rd_account_type", type);
    
    try {
      await api.post("/users/account-type", { account_type: type });
    } catch (err) {
      // Silently fail - don't break the flow
      console.error("Failed to set account type:", err);
    } finally {
      setSettingAccountType(false);
    }
  };

  // Retry fetching insight data with backoff protection
  const handleRetryAnalysis = async () => {
    // Guard: prevent retry while loading or in cooldown
    if (isAnalyzing) {
      console.log("Already analyzing, skipping retry");
      return;
    }
    
    // Guard: check cooldown
    if (cooldownRemaining > 0) {
      toast.error(`Please wait ${cooldownRemaining}s before trying again`);
      return;
    }
    
    // Guard: max retries
    if (retryCount >= 5) {
      toast.error("Maximum retries reached. Please refresh the page.");
      return;
    }
    
    setRetryCount(prev => prev + 1);
    
    // Set minimum 10s cooldown after manual retry
    setCooldownRemaining(10);
    setIsRateLimited(true);
    
    try {
      const storesRes = await api.get("/shopify/stores");
      const stores = storesRes.data?.stores || [];
      if (stores.length > 0) {
        const success = await fetchInsightData(stores[0].store_id, true);
        if (success) {
          toast.success("Analysis complete!");
          setRetryCount(0);
        }
      }
    } catch (err) {
      toast.error("Failed to retry analysis");
    }
  };

  const handleStartTrial = async () => {
    if (!billingEnabled) {
      setShowBillingPending(true);
      return;
    }
    
    setCheckoutLoading(true);
    
    // Get selected plan from sessionStorage (set on pricing page)
    const selectedPlan = sessionStorage.getItem('selected_plan') || 'operator';
    
    console.log(`🛒 Starting Shopify billing: plan=${selectedPlan}`);
    
    try {
      // Use Shopify Billing API
      const res = await api.post("/billing/shopify/create-subscription", { 
        plan: selectedPlan
      });
      
      if (res.data.billing_pending || res.data.billing_enabled === false) {
        setBillingEnabled(false);
        setShowBillingPending(true);
        setCheckoutLoading(false);
        return;
      }
      
      // Redirect to Shopify confirmation URL
      if (res.data.confirmation_url) {
        console.log(`🔗 Redirecting to Shopify billing: ${res.data.confirmation_url}`);
        // Clear checkout preferences
        sessionStorage.removeItem('selected_plan');
        sessionStorage.removeItem('billing_interval');
        window.location.href = res.data.confirmation_url;
      } else {
        toast.error("Failed to create subscription. Please try again.");
        setCheckoutLoading(false);
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const errorMessage = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errorMessage);
      setCheckoutLoading(false);
    }
  };

  const handleNotifyMe = async () => {
    setRegisteringIntent(true);
    try {
      const storeId = insightData?.store_id;
      await api.post("/billing/notify-intent", { store_id: storeId });
      setNotifyRegistered(true);
      toast.success("You're on the list!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setRegisteringIntent(false);
    }
  };

  // Determine which mode to show
  const isAgencyMode = accountType === "agency";
  const merchantInsights = buildMerchantInsights(insightData);
  const hasRealInsights = merchantInsights.length > 0 && merchantInsights.some(i => i.hasRealData);
  const isDataProcessing = insightData?.is_processing || isAnalyzing;
  const needsReauth = insightData?.needs_reauth;
  const isInsufficientData = insightData?.is_insufficient_data;

  // For agency mode, filter optional cards based on real data
  const agencyInsights = isAgencyMode ? AGENCY_LOCKED_INSIGHTS.filter(insight => {
    if (insight.optional) {
      // Only show optional cards if we have real signal
      return insightData?.product_opportunity?.products_analyzed > 0;
    }
    return true;
  }).slice(0, 3) : []; // Max 3 cards

  // Handle reconnect - redirect to onboarding
  const handleReconnect = () => {
    navigate("/onboarding", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin mx-auto mb-4" />
          <p className="text-text-muted">Loading your insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          
          <div className="flex items-center gap-4">
            <span className="text-text-muted text-sm hidden md:block">{user?.email}</span>
            {isAffiliate && (
              <Link to="/affiliates/dashboard">
                <Button variant="ghost" className="text-text-muted hover:text-white text-sm" data-testid="affiliate-dashboard-btn">
                  <Gift className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">Affiliate Dashboard</span>
                  <span className="sm:hidden">Affiliate</span>
                </Button>
              </Link>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-text-muted hover:text-white"
              onClick={logout}
              data-testid="logout-btn"
            >
              Sign out
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-full bg-[#D4F34A] text-black flex items-center justify-center font-bold text-sm">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div className="w-16 h-0.5 bg-[#D4F34A]"></div>
            <div className="w-8 h-8 rounded-full bg-[#D4F34A] text-black flex items-center justify-center font-bold text-sm">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div className="w-16 h-0.5 bg-[#D4F34A]"></div>
            <div className="w-8 h-8 rounded-full bg-[#D4F34A] text-black flex items-center justify-center font-bold text-sm">3</div>
          </div>

          {/* Header - Mode-specific copy */}
          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {isInsufficientData
                ? "Your store is connected"
                : isDataProcessing 
                  ? "Analyzing your store data..." 
                  : isAgencyMode
                    ? "We found clear opportunities in this store"
                    : "Based on your store data, we found these opportunities"
              }
            </h1>
            <p className="text-text-muted text-lg max-w-2xl mx-auto">
              {isInsufficientData
                ? "We'll generate insights once there's enough order data to analyze."
                : isDataProcessing 
                  ? "This usually takes a few moments. Your real insights will appear shortly."
                  : isAgencyMode
                    ? "These are the same patterns we see across high-performing Shopify stores - prioritized for impact."
                    : "Start your free trial to unlock the full analysis and get weekly insights delivered to your inbox."
              }
            </p>
            {/* Trust line */}
            <p className="text-xs text-text-muted mt-3 flex items-center justify-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              Read-only access. No changes made to your store.
            </p>
          </div>

          {/* Insufficient Data State - Clear message, still allow subscription */}
          {isInsufficientData && !needsReauth && (
            <Card className="bg-surface border-[#262626] mb-6" data-testid="insufficient-data-card">
              <CardContent className="py-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#262626] flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-text-muted" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">
                    Not enough order data yet
                  </h3>
                  <p className="text-text-muted text-sm mb-4 max-w-md mx-auto">
                    Connect a store with recent orders (last 14 days) or add a few test orders, then retry.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetryAnalysis}
                    disabled={isAnalyzing || cooldownRemaining > 0}
                    className="border-[#262626] text-white hover:bg-[#262626] disabled:opacity-50"
                  >
                    {isAnalyzing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : cooldownRemaining > 0 ? (
                      <>
                        <Clock className="w-4 h-4 mr-2" />
                        Wait {cooldownRemaining}s
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Check again
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* P0: Needs Reauth - Shopify connection expired */}
          {needsReauth && (
            <Card className="bg-surface border-amber-500/30 mb-6" data-testid="needs-reauth-card">
              <CardContent className="py-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                    <PlugZap className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">
                    Shopify connection expired
                  </h3>
                  <p className="text-text-muted text-sm mb-6 max-w-md mx-auto">
                    {insightData?.message || "Your store connection needs to be refreshed. This only takes a moment."}
                  </p>
                  <Button
                    onClick={handleReconnect}
                    className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
                    data-testid="reconnect-store-btn"
                  >
                    <PlugZap className="w-4 h-4 mr-2" />
                    Reconnect store
                  </Button>
                  <p className="text-xs text-text-muted mt-4">
                    Your data and settings will be preserved.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Processing State */}
          {isDataProcessing && !needsReauth && (
            <Card className="bg-surface border-[#262626] mb-6" data-testid="processing-card">
              <CardContent className="py-8">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 text-[#D4F34A] animate-spin mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-bold mb-2">
                    {insightData?.is_rate_limited 
                      ? "Please wait before trying again"
                      : insightData?.message || "Analyzing your store data..."}
                  </h3>
                  <p className="text-text-muted text-sm mb-4">
                    {insightData?.is_rate_limited 
                      ? `Too many requests. Try again in ${cooldownRemaining || insightData?.retry_after || 15} seconds.`
                      : "We're pulling your real metrics to generate personalized insights."}
                  </p>
                  {retryCount < 5 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRetryAnalysis}
                      disabled={isAnalyzing || cooldownRemaining > 0}
                      className="border-[#262626] text-white hover:bg-[#262626] disabled:opacity-50"
                    >
                      {isAnalyzing ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Checking...
                        </>
                      ) : cooldownRemaining > 0 ? (
                        <>
                          <Clock className="w-4 h-4 mr-2" />
                          Wait {cooldownRemaining}s
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Check again
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Real Instant Insight - Biggest Finding */}
          {!isDataProcessing && !needsReauth && insightData && insightData.biggest_insight && (
            <Card className="bg-surface border-[#D4F34A]/30 mb-6" data-testid="instant-insight-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-[#D4F34A]" />
                  </div>
                  <span className="text-xs font-medium text-[#D4F34A] uppercase tracking-wide">
                    {isAgencyMode ? "Primary Opportunity" : "Your Biggest Opportunity"}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">
                  {insightData.biggest_insight.title}
                </h3>
                <p className="text-text-muted mb-4">
                  {insightData.biggest_insight.why_it_matters || insightData.biggest_insight.description}
                </p>
                {/* Locked details indicator */}
                <div className="flex items-center gap-2 text-sm border-t border-[#262626] pt-4 mt-4">
                  <Lock className="w-4 h-4 text-text-muted" />
                  <span className="text-text-muted">
                    {isAgencyMode ? "Full recommendation unlocked after starting trial" : "Full analysis unlocked after starting your trial."}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Account Type Prompt - ONLY after first insight, inline, non-blocking */}
          {!isDataProcessing && !needsReauth && showAccountTypePrompt && !accountTypeSet && insightData?.biggest_insight && (
            <Card className="bg-surface border-[#262626] mb-6" data-testid="account-type-prompt">
              <CardContent className="py-5">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-sm text-text-muted">
                    Which best describes you?
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetAccountType("merchant")}
                      disabled={settingAccountType}
                      className="border-[#262626] text-white hover:bg-[#262626] hover:border-[#D4F34A]/50"
                      data-testid="account-type-merchant"
                    >
                      <Store className="w-4 h-4 mr-2" />
                      I run this store
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetAccountType("agency")}
                      disabled={settingAccountType}
                      className="border-[#262626] text-white hover:bg-[#262626] hover:border-[#D4F34A]/50"
                      data-testid="account-type-agency"
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      I manage stores for clients
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* AGENCY MODE: Locked Insight Cards */}
          {!isDataProcessing && !needsReauth && isAgencyMode && agencyInsights.length > 0 && (
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {agencyInsights.map((insight) => {
                const IconComponent = insight.icon;
                return (
                  <Card 
                    key={insight.id} 
                    className="bg-surface border-[#262626] relative overflow-hidden"
                    data-testid={`agency-insight-${insight.id}`}
                  >
                    <CardContent className="pt-6 pb-6">
                      {/* Card Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-lg ${insight.iconBg} flex items-center justify-center`}>
                          <IconComponent className={`w-4 h-4 ${insight.iconColor}`} />
                        </div>
                        <span className={`text-xs font-medium ${insight.iconColor} uppercase tracking-wide`}>
                          {insight.label}
                        </span>
                      </div>
                      
                      {/* Title */}
                      <h3 className="font-heading text-base font-bold mb-2 leading-tight">
                        {insight.title}
                      </h3>
                      
                      {/* Body */}
                      <p className="text-sm text-text-muted mb-3 leading-relaxed">
                        {insight.body}
                      </p>
                      
                      {/* Agency helper text */}
                      <p className="text-xs text-text-muted/70 italic mb-4">
                        {insight.agencyHelper}
                      </p>
                      
                      {/* Locked Section */}
                      <div className="relative mt-4 pt-4 border-t border-[#262626]">
                        <div className="absolute inset-0 bg-[#0a0a0a]/95 z-10 flex flex-col items-center justify-center rounded-b-lg">
                          <Lock className="w-5 h-5 text-text-muted mb-1" />
                          <span className="text-xs text-text-muted">{insight.lockLabel}</span>
                        </div>
                        
                        <div className="opacity-20 pointer-events-none">
                          <div className="h-3 bg-[#262626] rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-[#262626] rounded w-1/2 mb-2"></div>
                          <div className="h-3 bg-[#262626] rounded w-2/3"></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* MERCHANT MODE: Locked Insight Cards */}
          {!isDataProcessing && !needsReauth && !isAgencyMode && hasRealInsights && (
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {merchantInsights.map((insight) => {
                const IconComponent = insight.icon;
                return (
                  <Card 
                    key={insight.id} 
                    className="bg-surface border-[#262626] relative overflow-hidden"
                    data-testid={`locked-insight-${insight.id}`}
                  >
                    <CardContent className="pt-6 pb-6">
                      {/* Card Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-lg ${insight.iconBg} flex items-center justify-center`}>
                          <IconComponent className={`w-4 h-4 ${insight.iconColor}`} />
                        </div>
                        <span className={`text-xs font-medium ${insight.iconColor} uppercase tracking-wide`}>
                          {insight.label}
                        </span>
                      </div>
                      
                      {/* Title */}
                      <h3 className="font-heading text-base font-bold mb-2 leading-tight">
                        {insight.title}
                      </h3>
                      
                      {/* Why it matters */}
                      <p className="text-sm text-text-muted mb-4 leading-relaxed">
                        {insight.whyItMatters}
                      </p>
                      
                      {/* Locked Details Section */}
                      <div className="relative mt-4 pt-4 border-t border-[#262626]">
                        <div className="absolute inset-0 bg-[#0a0a0a]/95 z-10 flex flex-col items-center justify-center rounded-b-lg">
                          <Lock className="w-5 h-5 text-text-muted mb-1" />
                          <span className="text-xs text-text-muted">Full details locked</span>
                        </div>
                        
                        <div className="opacity-20 pointer-events-none">
                          <div className="h-3 bg-[#262626] rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-[#262626] rounded w-1/2 mb-2"></div>
                          <div className="h-3 bg-[#262626] rounded w-2/3"></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Additional insights notice (edge case) */}
          {!isDataProcessing && !needsReauth && insightData && !hasRealInsights && !isAgencyMode && insightData.biggest_insight && (
            <Card className="bg-surface border-[#262626] mb-6" data-testid="additional-insights-card">
              <CardContent className="py-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-text-muted flex-shrink-0" />
                  <p className="text-sm text-text-muted">
                    Additional insights are being generated - available shortly in the full report.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* AGENCY MODE: Lock Explanation */}
          {!isDataProcessing && !needsReauth && isAgencyMode && (
            <div className="text-center mb-8">
              <p className="text-sm text-text-muted">
                This preview shows what we found.<br />
                The full report explains why it's happening and what to do next - step by step.
              </p>
            </div>
          )}

          {/* Unlock CTA Section - Allow subscription even with insufficient data */}
          {!needsReauth && (
          <Card className="bg-gradient-to-r from-[#D4F34A]/5 to-transparent border-[#D4F34A]/20" data-testid="unlock-cta-card">
            <CardContent className="py-10">
              <div className="text-center max-w-xl mx-auto">
                <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-5">
                  <Zap className="w-8 h-8 text-[#D4F34A]" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-3">
                  {isInsufficientData 
                    ? "Start your trial now"
                    : isAgencyMode 
                      ? "Unlock the full report" 
                      : "Unlock your full report"
                  }
                </h2>
                <p className="text-text-muted mb-6">
                  {isInsufficientData
                    ? "We're monitoring your store and will generate insights once there's enough data. Insights unlock automatically when thresholds are met."
                    : isAgencyMode 
                      ? "Get the complete analysis, client-ready recommendations, and weekly insights delivered automatically."
                      : "Start your 7-day free trial to see complete analysis details, get actionable recommendations, and receive weekly reports every Monday."
                  }
                </p>
                
                <Button
                  onClick={handleStartTrial}
                  disabled={checkoutLoading}
                  className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6 px-10 text-lg"
                  data-testid="start-trial-btn"
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Opening checkout...
                    </>
                  ) : (
                    <>
                      Start free trial
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-text-muted mt-5 flex items-center justify-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  Read-only access · No store changes · Cancel anytime
                </p>
                
                {/* Agency nudge */}
                {isAgencyMode && !isInsufficientData && (
                  <p className="text-xs text-text-muted/70 mt-3">
                    Many agencies use ReplyDriven across multiple client stores.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
          )}

          {/* What you get (hide when needs reauth) */}
          {!needsReauth && (
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Weekly Store Analysis</p>
                <p className="text-sm text-text-muted">Automated profit leak detection every Monday</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Email Reports</p>
                <p className="text-sm text-text-muted">Insights delivered straight to your inbox</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Actionable Recommendations</p>
                <p className="text-sm text-text-muted">Clear steps to fix each profit leak</p>
              </div>
            </div>
          </div>
          )}
        </div>
      </main>

      {/* Billing Pending Modal */}
      {showBillingPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => !notifyRegistered && setShowBillingPending(false)}
          />
          
          <div className="relative bg-surface border border-[#262626] rounded-2xl p-8 max-w-md w-full text-center shadow-2xl" data-testid="billing-pending-modal">
            {notifyRegistered ? (
              <>
                <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8 text-[#D4F34A]" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-3">
                  You're on the list
                </h2>
                <p className="text-text-muted mb-6">
                  We'll email you as soon as billing opens.
                </p>
                <p className="text-sm text-text-muted mb-6">
                  Your instant insight is saved. You can close this window and come back anytime.
                </p>
                <Button
                  onClick={() => setShowBillingPending(false)}
                  variant="outline"
                  className="border-[#262626] text-white hover:bg-[#262626]"
                >
                  Got it
                </Button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-5">
                  <Clock className="w-8 h-8 text-[#D4F34A]" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-3">
                  Billing is opening shortly
                </h2>
                <p className="text-text-muted mb-6">
                  You've unlocked a real insight from your store. We're finishing payment activation and will notify you the moment billing opens.
                </p>
                
                <Button
                  onClick={handleNotifyMe}
                  disabled={registeringIntent}
                  className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-5 px-8 text-base w-full"
                  data-testid="notify-me-btn"
                >
                  {registeringIntent ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Notify me when billing opens
                    </>
                  )}
                </Button>
                
                <button
                  onClick={() => setShowBillingPending(false)}
                  className="mt-4 text-sm text-text-muted hover:text-white transition-colors"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingPreview;
